import React, { FC } from 'react'
import LayoutDefault from '@/components/layouts/Layout'
import SectionHeader from '@/components/modules/SectionHeader'
import PortfolioItem from '@/components/modules/PortfolioItem'
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa'
import TitleShadow from '@/components/modules/TitleShadow'
import Link from 'next/link'
import { getAllPostsWithSlug, getPostBySlug } from '@/service/portfolio'
import { GetStaticPaths, GetStaticProps } from 'next'
import { allPosts, SinglePost } from '@/types'
import PageLoading from '@/components/modules/PageLoading'
import { useRouter } from 'next/router'

export interface AdjacentPosts {
  previous: { slug: string; title: string } | null
  next: { slug: string; title: string } | null
}

interface Post {
  post: SinglePost
  postIndex: number
  allPosts: allPosts[]
  categorie: string
}

const PortfolioItemPage: FC<Post> = (props: Post) => {
  const { post, postIndex, allPosts, categorie } = props
  const route = useRouter()

  const getAdjacentPosts = () => {
    // allPosts.edges[postIndex + 1].node.title for next post title
    // allPosts.edges[postIndex - 1].node.title for prev post title
    return (
      <div className='navigation-post grid grid-cols-2'>
        {postIndex <= 0 ? (
          <div className='post-prev-post'></div>
        ) : (
          <div className='post-prev-post'>
            <Link href={`/${allPosts[postIndex - 1]?.slug}`} prefetch={false}>
              <div className='flex items-center'>
                <span className='mr-2 bg-primary p-3'>
                  <FaAngleLeft size={20} className='text-white' />
                </span>
                <div className='post-next-prev-content'>
                  <span className='block text-xs'>ก่อนหน้า</span>
                </div>
              </div>
            </Link>
          </div>
        )}
        {postIndex >= allPosts.length - 1 ? (
          <div className='post-next-post'></div>
        ) : (
          <div className='post-next-post'>
            <Link href={`/${allPosts[postIndex + 1]?.slug}`} prefetch={false}>
              <div className='flex items-center justify-end'>
                <span className='order-2 ml-2 bg-primary p-3'>
                  <FaAngleRight size={20} className='text-white' />
                </span>
                <div className='post-next-prev-content order-1 text-right'>
                  <span className='block text-xs'>ถัดไป</span>
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {route.isFallback ? (
        <PageLoading />
      ) : (
        <LayoutDefault>
          <SectionHeader
            title='portfolio'
            bgColors='#1990FF'
            bgImage='/images/default_banner_header.webp'
            bgImageSm='/images/default_banner_header-sm.webp'
            bgImageWidth={1214}
            bgImageHeight={534}
          />
          <div className='container mx-auto pb-12 pt-12'>
            <div className='mb-24 grid grid-cols-4 gap-4 lg:gap-8'>
              <div className='col-span-4'>
                <h1 className='text-4xl font-bold'>{post.title}</h1>
              </div>
              <div className='col-span-4 space-y-8 lg:col-span-3'>
                <div dangerouslySetInnerHTML={{ __html: post.content }} className='prose' />
              </div>
              <div className='col-span-4 hidden space-y-8 lg:col-span-1 lg:block'>
                <div className='space-y-2'>
                  <p>หมวดหมู่ : {categorie}</p>
                </div>
              </div>
            </div>
            {getAdjacentPosts()}
          </div>
          <div className='bg-author-base pt-24 lg:pt-44'>
            <div className='container mx-auto space-y-8 px-4 pb-14 lg:pt-44 xl:pt-0'>
              <TitleShadow
                prologue='ผลงาน'
                colorPrologue='rgba(0,0,0,.08)'
                title={<>ผลงาน</>}
                colorTitle='#000000'
                align='center'
              />
            </div>
            <div className='grid grid-cols-2 lg:grid-cols-4'>
              <PortfolioItem
                title='Atime.live'
                categories='website'
                serviceImg='/images/service/website/Atime_live.webp'
                serviceBg='#FFCA64'
                slug='/portfolio/atime-live/'
              />
              <PortfolioItem
                title='Spicydisc'
                categories='website'
                serviceImg='/images/service/website/Spicydisc.webp'
                serviceBg='#FFCA64'
                slug='/portfolio/spicydisc/'
              />
              <PortfolioItem
                title='Smaklen.club'
                categories='website'
                serviceImg='/images/service/website/Smaklen_club.webp'
                serviceBg='#FFCA64'
                slug='/portfolio/smaklen-club/'
              />
              <PortfolioItem
                title='Brandthink shop'
                categories='website'
                serviceImg='/images/service/website/Brandthink_shop.webp'
                serviceBg='#FFCA64'
                slug='/portfolio/brandthink-shop/'
              />
            </div>
          </div>
        </LayoutDefault>
      )}
    </>
  )
}

type CateData = {
  node: {
    name: string
    slug: string
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug ?? ''

  const data = await getPostBySlug(slug as string)
  const allPosts = await getAllPostsWithSlug()
  const postIndex = allPosts?.nodes.map((nodes: any) => nodes.slug).indexOf(slug)
  const postCateName = data?.categories?.edges?.map((cate: CateData) => cate.node.name).join(', ')

  if (!data) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      post: data ?? '',
      postIndex,
      allPosts: allPosts?.nodes ?? [],
      categorie: postCateName,
    },
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const allPosts = await getAllPostsWithSlug()
  // console.log('allPosts', allPosts.edges.map(({ node }: any) => `/portfolio/${node.slug}`) ?? [])
  return {
    paths: allPosts?.nodes.map((nodes: any) => `/portfolio/${nodes.slug}`) ?? [],
    fallback: true,
  }
}

export default PortfolioItemPage
